import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { Modals } from '@lib/enums/modals';
import useModalManagement from '@lib/hooks/useModalManagement';
import SiteAdd from 'routes/Sites/Site/SiteAdd';
import { SiteViewMode } from 'routes/Sites/enums';
import SiteCodeOfConduct from 'routes/Sites/Site/SiteCodeOfConduct';
import SiteView from 'routes/Sites/Site/SiteView';
import SiteEdit from 'routes/Sites/Site/SiteEdit';
import useSiteActions from './utils/useSiteActions';

const modals = [
  Modals.UserFormModal,
  Modals.SiteDestroyModal,
  Modals.SiteCodeOfConductDestroyModal,
];

interface Props {
  viewMode: SiteViewMode;
}

function SiteContainer({ viewMode }: Props) {
  const { id = '' } = useParams();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const useModalManagementResult = useModalManagement(modals);

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) ===
    AccessLevelEnum.Read;

  const useSiteActionsResult = useSiteActions({
    id,
    formRef,
    useModalManagementResult,
    viewMode,
  });
  const { siteQuery } = useSiteActionsResult;

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: siteQuery.data?.site as Values,
        })}
      />
      {viewMode === SiteViewMode.Add && (
        <SiteAdd
          formRef={formRef}
          useModalManagementResult={useModalManagementResult}
          useSiteActionsResult={useSiteActionsResult}
        />
      )}
      {viewMode === SiteViewMode.Edit && (
        <SiteEdit
          formRef={formRef}
          useModalManagementResult={useModalManagementResult}
          useSiteActionsResult={useSiteActionsResult}
        />
      )}
      {viewMode === SiteViewMode.View && (
        <SiteView
          isViewOnly={isViewOnly}
          useModalManagementResult={useModalManagementResult}
          useSiteActionsResult={useSiteActionsResult}
        />
      )}
      {(viewMode === SiteViewMode.ViewCodeOfConduct ||
        viewMode === SiteViewMode.EditCodeOfConduct) && (
        <SiteCodeOfConduct
          useModalManagementResult={useModalManagementResult}
          useSiteActionsResult={useSiteActionsResult}
          viewMode={viewMode}
        />
      )}
    </>
  );
}

export default SiteContainer;
